import React from "react";
import { captureException } from "../sentry";
import { maybeReloadForChunkError } from "../util/chunkReloadHandler";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Stale-chunk failures after a deploy land here (React.lazy re-throws on
    // render). Auto-reload past them — they're deployment noise, not bugs.
    // If the cooldown blocks the reload, fall through so Sentry sees the
    // repeated failure and the user gets the manual reload screen.
    if (maybeReloadForChunkError(error)) return;
    captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <section className="hero is-fullheight">
          <div className="hero-body">
            <div className="container has-text-centered">
              <p className="title">Something went wrong — please reload the page</p>
              <p className="subtitle">An unexpected error occurred.</p>
              <button
                className="button is-primary"
                onClick={() => window.location.reload()}
              >
                Reload
              </button>
            </div>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}
